import React, { useState, useMemo } from 'react';
import { ImageIcon } from '@/components/icons';
import {
  useAnnouncements,
  useHighlightedAnnouncement,
  useDeleteAnnouncement,
  useHighlightAnnouncement,
  useContentCategories
} from '@/lib/api/hooks/announcement';
import { showToast } from '@/lib/toast';
import { Button, ActionDropdown, DataTable, type DropdownAction } from '@/components/ui';
import type { Announcement, ContentCategory } from '@/lib/api/types';
import { useDebounce } from '@/hooks/helper-function';
import { Loader } from '@/components/ui/Loader';
import AnnouncementModal from '@/pages/announcement/modals/AnnouncementModal';
import DeleteAnnouncementModal from '@/pages/announcement/modals/DeleteAnnouncementModal';
import HighlightAnnouncementModal from '@/pages/announcement/modals/HighlightAnnouncementModal';

const Announcements: React.FC = () => {
  const [page] = useState(1);
  const [pageSize] = useState(50);
  const [searchValue, setSearchValue] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [highlightModalOpen, setHighlightModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<{ id: string; title: string } | null>(null);
  const [contentCategory, setContentCategory] = useState<string>('');
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(searchValue, 3000);

  // Fetch highlighted announcement (current highlight)
  const { data: highlightedData, isLoading: isLoadingHighlighted } = useHighlightedAnnouncement();
  const highlightedAnnouncement = highlightedData || null;

  // Fetch previous highlights (non-highlighted announcements)
  const { data: announcementsData, isLoading, error } = useAnnouncements(
    { page, size: pageSize },
    {
      highlighted: false,
      ...(contentCategory && { contentCategory })
    }
  );

  // Get content categories for filter
  const { data: categoriesData } = useContentCategories();
  const categories = categoriesData?.data || [];

  const { mutate: deleteAnnouncement, isPending: isDeleting } = useDeleteAnnouncement();
  const { mutate: highlightAnnouncement, isPending: isHighlighting } = useHighlightAnnouncement();

  // Client-side search filtering since API might not support title filter
  const filteredAnnouncements = useMemo(() => {
    const announcements = announcementsData?.data?.data || [];
    if (!debouncedSearch) return announcements;
    return announcements.filter((announcement: Announcement) =>
      announcement.title.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [announcementsData?.data?.data, debouncedSearch]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatContentCategory = (category: string) => {
    return category
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleDeleteClick = (announcement: { announcementId: string; title: string }) => {
    setSelectedAnnouncement({ id: announcement.announcementId, title: announcement.title });
    setDeleteModalOpen(true);
  };

  const handleHighlightClick = (announcement: { announcementId: string; title: string }) => {
    setSelectedAnnouncement({ id: announcement.announcementId, title: announcement.title });
    setHighlightModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedAnnouncement) return;

    deleteAnnouncement(selectedAnnouncement.id, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Announcement deleted successfully!');
          setDeleteModalOpen(false);
          setSelectedAnnouncement(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const confirmHighlight = () => {
    if (!selectedAnnouncement) return;

    highlightAnnouncement({
      announcementId: selectedAnnouncement.id,
      data: { highlight: true }
    }, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Announcement highlighted successfully!');
          setHighlightModalOpen(false);
          setSelectedAnnouncement(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to highlight: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const handleEdit = (announcementId: string) => {
    setEditingAnnouncementId(announcementId);
    setAnnouncementModalOpen(true);
  };

  const handleCreateNew = () => {
    setEditingAnnouncementId(undefined);
    setAnnouncementModalOpen(true);
  };

  const handleModalSuccess = () => {
    // Modal will close itself, but we can refresh data if needed
    setAnnouncementModalOpen(false);
    setEditingAnnouncementId(undefined);
  };

  const handleModalClose = () => {
    setAnnouncementModalOpen(false);
    setEditingAnnouncementId(undefined);
  };

  const getDropdownActions = (announcement: Announcement): DropdownAction[] => [
    ...(announcement.announcementType === "MANUAL" ? [{
      label: 'Edit',
      onClick: (e: { stopPropagation: () => void; }) => {
        e.stopPropagation();
        handleEdit(announcement.announcementId);
      }
    }] : []),
    ...(!announcement.highlighted
      ? [{
          label: 'Highlight Announcement',
          onClick: (e: { stopPropagation: () => void; }) => {
            e.stopPropagation();
            handleHighlightClick(announcement);
          }
        }]
      : []),
    {
      label: 'Delete',
      onClick: (e: { stopPropagation: () => void; }) => {
        e.stopPropagation();
        handleDeleteClick(announcement);
      },
      variant: 'danger' as const,
      disabled: isDeleting
    }
  ];

  return (
    <>
      <HighlightAnnouncementModal
        isOpen={highlightModalOpen}
        isHighlighting={isHighlighting}
        title={selectedAnnouncement?.title}
        onClose={() => setHighlightModalOpen(false)}
        onConfirm={confirmHighlight}
      />
      <DeleteAnnouncementModal
        isOpen={deleteModalOpen}
        isDeleting={isDeleting}
        title={selectedAnnouncement?.title}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
      <AnnouncementModal
        isOpen={announcementModalOpen}
        onClose={handleModalClose}
        announcementId={editingAnnouncementId}
        onSuccess={handleModalSuccess}
      />
      <div className="space-y-6">
        {/* Create Announcement Button */}
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={handleCreateNew}
          >
            Create Announcement
          </Button>
        </div>

        {/* Current Highlight Section */}
        <div className="space-y-4">
          <Button variant="primary" className="bg-wine-100 hover:bg-wine-200 py-3 px-4 text-white">
            Current Highlight
          </Button>

          {isLoadingHighlighted ? (
            <Loader size="md" variant="wine" message="Loading highlighted announcement..." />
          ) : highlightedAnnouncement ? (
            <div 
              className="bg-[#FAFAFA] border border-gray-200 rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => {
                if (highlightedAnnouncement.announcementType === "MANUAL") {
                  handleEdit(highlightedAnnouncement.announcementId)
                } else {
                  showToast.info('Only manual announcements can be edited. Please create a new announcement if you want to edit content for linked announcements.');
                }
              }}
            >
              {highlightedAnnouncement.imageUrl || highlightedAnnouncement.linkedContent?.imageUrl ? (
                <img
                  src={highlightedAnnouncement.imageUrl || highlightedAnnouncement.linkedContent?.imageUrl}
                  alt={highlightedAnnouncement.title}
                  className="w-20 h-16 object-cover rounded-md flex-shrink-0 bg-gray-200/20"
                />
              ) : (
                <div className="w-20 h-16 bg-gray-200/20 rounded-md flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm truncate">{highlightedAnnouncement.title}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(highlightedAnnouncement.createdAt)} | {highlightedAnnouncement.createdByUsername}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-2 py-1 bg-[#047857] text-white text-xs rounded-md">
                    {formatContentCategory(highlightedAnnouncement.contentCategory)}
                  </span>
                  <span className="px-2 py-1 bg-wine-100 text-white text-xs rounded-md">
                    {(highlightedAnnouncement.announcementType === 'LINKED' ? 'Linked' : 'Manual')}
                  </span>
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <ActionDropdown
                  id={highlightedAnnouncement.announcementId}
                  actions={getDropdownActions(highlightedAnnouncement)}
                />
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-sm text-gray-500">No highlighted announcement</p>
            </div>
          )}
        </div>


        <div className="space-y-4">
          <div className="flex justify-between">
          <Button variant="primary" className="bg-wine-100 hover:bg-wine-200 py-3 px-4 text-white">
            Previous Highlight
          </Button>

          <div className="flex items-center gap-4">
            <div className="relative">
              <select
                value={contentCategory}
                onChange={(e) => setContentCategory(e.target.value)}
                className="select-wine appearance-none bg-[#FAFAFA] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 pr-8 w-full"
              >
                <option value="">This month</option>
                {categories.map((cat: ContentCategory) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.displayName}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          </div>

          <DataTable
            data={filteredAnnouncements}
            columns={[]}
            searchable
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            filterable={false}
            loading={isLoading}
            loadingMessage="Loading announcements..."
            error={error}
            errorTitle="Error Loading Announcements"
            emptyTitle="No Previous Highlights"
            emptyMessage="You are yet to create previous highlights"
            emptyAction={
              <Button
                variant="primary"
                onClick={handleCreateNew}
              >
                Create Announcement
              </Button>
            }
            renderRow={(announcement: Announcement) => (
              <div
                key={announcement.announcementId}
                onClick={() => {
                  if (announcement.announcementType === "MANUAL") {
                    handleEdit(announcement.announcementId);
                  } else {
                    showToast.info('Only manual announcements can be edited. Please create a new announcement if you want to edit content for linked announcements.');
                  }
                }}
                className={`flex items-center gap-4 p-4 cursor-pointer bg-[#FAFAFA] rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors ${
                  isDeleting ? 'opacity-50' : ''
                }`}
              >
                {announcement.imageUrl ?? announcement.linkedContent?.imageUrl ? (
                  <img
                    src={announcement.imageUrl ?? announcement.linkedContent?.imageUrl}
                    alt={announcement.title}
                    className="w-20 h-16 object-cover rounded-md flex-shrink-0 bg-gray-200/20"
                  />
                ) : (
                  <div className="w-20 h-16 bg-gray-200/20 rounded-md flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 text-sm truncate">{announcement.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(announcement.createdAt)} | {announcement.createdByUsername}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-1 bg-[#047857] text-white text-xs rounded-md">
                      {formatContentCategory(announcement.contentCategory)}
                    </span>
                    <span className="px-2 py-1 bg-wine-100 text-white text-xs rounded-md">
                      {(announcement.announcementType === 'LINKED' ? 'Linked' : 'Manual')}
                    </span>
                  </div>
                </div>
                <ActionDropdown id={announcement.announcementId} actions={getDropdownActions(announcement)} />
              </div>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default Announcements;

